import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ShieldCheck } from "lucide-react";
import { API } from "../../context/AuthContext";

const inputCls = "w-full bg-gray-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-100 text-center tracking-[0.4em] font-mono focus:border-[#f59e0b]/50 focus:outline-none";

export default function VaultDialog({ mode, onUnlocked, onSetup, onClose }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(pin)) {
      toast.error("PIN must be 4-8 digits.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "setup") {
        if (pin !== confirm) {
          toast.error("PINs don't match.");
          setBusy(false);
          return;
        }
        await axios.post(`${API}/vault/setup`, { pin });
        toast.success("Vault created. Your PIN is the only key — don't lose it.");
        onSetup();
      } else {
        const res = await axios.post(`${API}/vault/unlock`, { pin });
        toast.success("Vault unlocked for 15 minutes.");
        onUnlocked(res.data.vault_token);
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vault error.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="vault-dialog">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#131f3a] border border-white/10 rounded-2xl p-6 w-full max-w-xs space-y-4 shadow-2xl">
        <div className="flex justify-between items-center">
          <h3 className="flex items-center gap-2 text-sm font-bold text-white uppercase tracking-wide font-display">
            <ShieldCheck className="w-4 h-4 text-[#f59e0b]" /> {mode === "setup" ? "Create Vault" : "Unlock Vault"}
          </h3>
          <button type="button" onClick={onClose} data-testid="vault-close-btn" className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[11px] text-gray-400 leading-relaxed">
          {mode === "setup"
            ? "Pick a 4-8 digit PIN. Vaulted evidence disappears from every timeline, report, and AI answer until you unlock it."
            : "Enter your PIN to reveal vaulted evidence for 15 minutes."}
        </p>
        <input type="password" inputMode="numeric" autoFocus maxLength={8} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="••••" className={inputCls} data-testid="vault-pin-input" />
        {mode === "setup" && (
          <input type="password" inputMode="numeric" maxLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))} placeholder="Confirm PIN" className={inputCls} data-testid="vault-pin-confirm-input" />
        )}
        <button type="submit" disabled={busy} data-testid="vault-submit-btn" className="w-full bg-[#f59e0b] hover:bg-[#fbbf24] text-gray-950 font-bold text-xs py-2.5 rounded-lg transition-colors duration-200 disabled:opacity-50">
          {busy ? "..." : mode === "setup" ? "Create Vault" : "Unlock"}
        </button>
      </form>
    </div>
  );
}
